import { type ReactNode, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { AppBar, Avatar } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';

import { Link } from '@components/link/Link.tsx';
import {
  APP_MAX_WIDTH,
  APP_MIN_WIDTH,
  HEADER_BODY_MARGIN_BOTTOM,
  HEADER_BODY_MARGIN_TOP,
  TRANSITION_DURATION,
} from '@assets/constants';
import { NavBarLinks, Path } from '@assets/enums';
import { useAuthContext, useCan } from '@assets/helpers/hooks';
import { SmallLogo } from '@assets/icons/small-logo.tsx';

import { Button } from '../../button/index.ts';
import { ResourcesMenu } from './ResourcesMenu.tsx';

type HeaderPT = {
  hideOutlet?: boolean;
  children?: ReactNode;
};

const HEADER_SCROLL_DISTANCE = 64;

const getHeaderScrollProgress = () => {
  return Math.min(Math.max(window.scrollY, 0) / HEADER_SCROLL_DISTANCE, 1);
};

export const Header = ({ children, hideOutlet = false }: HeaderPT) => {
  const { pathname } = useLocation();
  const { isAuth, authData } = useAuthContext();
  const { can, hasPermissions } = useCan();
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    let animationFrameId: number | null = null;

    const updateScrollProgress = () => {
      animationFrameId = null;
      setScrollProgress(getHeaderScrollProgress());
    };

    const handleScroll = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(updateScrollProgress);
    };

    updateScrollProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  const headerBackgroundAlpha = 1 - scrollProgress * 0.16;
  const headerBlur = Math.round(scrollProgress * 18);
  const headerShadowOpacity = scrollProgress * 0.08;
  return (
    <>
      <AppBar
        component="nav"
        position="sticky"
        sx={{
          minWidth: APP_MIN_WIDTH,
          top: 0,
          backgroundColor: (theme) => alpha(theme.palette.backgroundColor, headerBackgroundAlpha),
          backdropFilter: `blur(${headerBlur}px)`,
          WebkitBackdropFilter: `blur(${headerBlur}px)`,
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.dark, scrollProgress * 0.12)}`,
          boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.dark, headerShadowOpacity)}`,
          transition: [
            `background-color ${TRANSITION_DURATION} ease`,
            `backdrop-filter ${TRANSITION_DURATION} ease`,
            `-webkit-backdrop-filter ${TRANSITION_DURATION} ease`,
            `border-color ${TRANSITION_DURATION} ease`,
            `box-shadow ${TRANSITION_DURATION} ease`,
          ].join(', '),
        }}
      >
        <Stack
          sx={{
            minHeight: '80px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 4,
            px: 10,
            maxWidth: APP_MAX_WIDTH,
            margin: '0 auto',
            width: '100%',
          }}
        >
          <Link to={Path.Home}>
            <SmallLogo />
          </Link>
          {isAuth && hasPermissions && (
            <Stack direction={'row'} alignSelf={'center'} justifySelf={'center'} flexWrap={'wrap'}>
              {Object.entries(NavBarLinks)
                .filter(([, path]) => path !== NavBarLinks.Users || can('user', 'read'))
                .map(([link, path]) => {
                  const isActive = pathname.split('/')[1] === link.toLowerCase();
                  return (
                    <Button
                      variantStyle={'tab'}
                      active={isActive}
                      component={Link}
                      key={link}
                      to={path}
                    >
                      {link}
                    </Button>
                  );
                })}
            </Stack>
          )}
          {isAuth && (
            <Stack direction="row" alignItems="center" spacing={4}>
              <ResourcesMenu />
              <Link to={Path.Profile}>
                <Avatar
                  sx={{ width: 48, height: 48, borderRadius: '13.7143px' }}
                  src={authData?.photo}
                />
              </Link>
            </Stack>
          )}
        </Stack>
      </AppBar>

      <Box
        component="main"
        sx={{
          minWidth: APP_MIN_WIDTH,
          maxWidth: APP_MAX_WIDTH,
          margin: '0 auto',
          px: 10,
          pb: `${HEADER_BODY_MARGIN_BOTTOM}px`,
          '--app-main-mt': `${HEADER_BODY_MARGIN_TOP}px`,
          '--app-main-pb': `${HEADER_BODY_MARGIN_BOTTOM}px`,
        }}
      >
        {!hideOutlet && <Outlet />}
        {children}
      </Box>
    </>
  );
};
